import { createStyles, makeUiStyles, Theme } from "../styles/styles";
import {
  IconButtonSizeModel,
  IconButtonVariantModel,
} from "./tools/IconButton.model";
import { fontDisplay, StylesIndexInjections } from "common-react";
import { layeredBackgroundMixin } from "../styles/mixins";

const DEFAULT_BORDER_WIDTH = 2;

const DEFAULT_ICON_WRAPPER_SMALL_SIZE = 32;
const DEFAULT_ICON_WRAPPER_DEFAULT_SIZE = 44;
const DEFAULT_ICON_WRAPPER_MEDIUM_SIZE = 24;

const generateIconWrapperSize = (
  size?: IconButtonSizeModel,
  variant?: IconButtonVariantModel,
) => {
  switch (size) {
    case "small":
      return variant === "primary"
        ? DEFAULT_ICON_WRAPPER_SMALL_SIZE
        : DEFAULT_ICON_WRAPPER_SMALL_SIZE - DEFAULT_BORDER_WIDTH;
    case "medium":
      return variant === "tertiary"
        ? DEFAULT_ICON_WRAPPER_MEDIUM_SIZE
        : DEFAULT_ICON_WRAPPER_MEDIUM_SIZE - DEFAULT_BORDER_WIDTH;
    case "default":
      return variant === "primary"
        ? DEFAULT_ICON_WRAPPER_DEFAULT_SIZE
        : DEFAULT_ICON_WRAPPER_DEFAULT_SIZE - DEFAULT_BORDER_WIDTH;
  }
};

interface IconButtonStylePropsModel {
  size?: IconButtonSizeModel;
  variant?: IconButtonVariantModel;
  label?: string;
}

const getIconWrapperSize = ({ size, variant }: IconButtonStylePropsModel) => generateIconWrapperSize(size, variant);

const getIconSize = ({ size }: IconButtonStylePropsModel) => {
  switch (size) {
    case "small":
      return 12;
    case "medium":
    case "default":
      return 16;
  }
};

const getButtonLabelSize = (theme: Theme, size?: IconButtonSizeModel) => {
  switch (size) {
    case "small":
      return fontDisplay("normal", theme.fontSize.s, 20);
    case "default":
      return fontDisplay("normal", theme.fontSize.base, 24);
  }
};

const getIconBackgroundColor = (
  { variant }: IconButtonStylePropsModel,
  theme: Theme,
) => {
  switch (variant) {
    case "primary":
      return theme.color.greys[80];
    case "secondary":
    case "tertiary":
      return "transparent";
  }
};

const getIconBackgroundColorHover = (theme: Theme) =>
  layeredBackgroundMixin({
    baseColor: theme.color.greys[80],
    overlayColor: theme.color.greys[0],
    overlayOpacity: 0.08,
  });

const getIconBackgroundColorPressed = (theme: Theme) =>
  layeredBackgroundMixin({
    baseColor: theme.color.greys[80],
    overlayColor: theme.color.greys[0],
    overlayOpacity: 0.16,
  });

const getIconColor = ({ variant }: IconButtonStylePropsModel, theme: Theme) => {
  switch (variant) {
    case "primary":
      return theme.color.greys[0];
    case "secondary":
      return theme.color.greys[80];
    case "tertiary":
      return theme.color.oranges[100];
  }
};

const getBorder = ({ variant }: IconButtonStylePropsModel, theme: Theme) => {
  switch (variant) {
    case "primary":
    case "tertiary":
      return "none";
    case "secondary":
      return `2px solid ${theme.color.greys[80]}`;
  }
};

const getIconFocusVisible = (
  { label }: IconButtonStylePropsModel,
  theme: Theme,
) => {
  if (label) {
    return "none";
  }
  return `2px solid ${theme.color.greys[80]}`;
};

const getButtonFocusVisible = (
  { label }: IconButtonStylePropsModel,
  theme: Theme,
) => {
  if (label) {
    return `2px solid ${theme.color.greys[80]}`;
  }
  return "none";
};

export default makeUiStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        padding: 0,
        display: "flex",
        "& .MuiIconButton-label": {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        },
        "&:active": {
          "& $iconWrapper": {
            ...getIconBackgroundColorPressed(theme),
          },
          "& $icon": {
            color: theme.color.greys[0],
          },
        },
        "&:hover": {
          background: "none",
          "& $iconWrapper": {
            ...getIconBackgroundColorHover(theme),
          },
          "& $icon": {
            color: theme.color.greys[0],
          },
        },
        "&:focus-visible": {
          outline: ({ label }: IconButtonStylePropsModel) =>
            getButtonFocusVisible({ label }, theme),
          outlineOffset: 2,
          borderRadius: 2,
          "& $iconWrapper": {
            outline: ({ label }: IconButtonStylePropsModel) =>
              getIconFocusVisible({ label }, theme),
            outlineOffset: 2,
          },
        },
        "&.Mui-focusVisible": {
          outline: "none",
        },
      },
      iconWrapper: {
        width: ({ size, variant }: IconButtonStylePropsModel) =>
          getIconWrapperSize({ size, variant }),
        height: ({ size, variant }: IconButtonStylePropsModel) =>
          getIconWrapperSize({ size, variant }),
        backgroundColor: ({ variant }: IconButtonStylePropsModel) =>
          getIconBackgroundColor({ variant }, theme),
        border: ({ variant }: IconButtonStylePropsModel) =>
          getBorder({ variant }, theme),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
      },
      icon: {
        width: ({ size }: IconButtonStylePropsModel) => getIconSize({ size }),
        height: ({ size }: IconButtonStylePropsModel) => getIconSize({ size }),
        color: ({ variant }: IconButtonStylePropsModel) =>
          getIconColor({ variant }, theme),
      },
      label: {
        font: ({ size }: IconButtonStylePropsModel) =>
          getButtonLabelSize(theme, size),
        color: theme.color.greys[80],
        padding: "0 4px",
        "&:empty": {
          display: "none",
        },
      },
      isDisabled: {
        opacity: 0.4,
        pointerEvents: "none",
      },
    }),
  { name: "IconButton", index: StylesIndexInjections.UI_ATOMS },
);
